import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUserSocket } from "@/hooks/useUserSocket";
import { Button } from "@/components/ui/button";
import { Phone, PhoneOff, Video } from "lucide-react";

const RING_TIMEOUT_MS = 45000;

// Plays a two-tone ring for as long as a call card is on screen.
function useRingtone(active) {
  useEffect(() => {
    if (!active) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    let ctx;
    try {
      ctx = new Ctx();
    } catch (err) {
      console.debug("ringtone unavailable", err);
      return;
    }
    const beep = (at, freq) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(0.16, at + 0.05);
      gain.gain.linearRampToValueAtTime(0, at + 0.4);
      osc.start(at);
      osc.stop(at + 0.45);
    };
    const ring = () => {
      const t = ctx.currentTime;
      beep(t, 880);
      beep(t + 0.5, 660);
    };
    ring();
    const iv = setInterval(ring, 2600);
    return () => {
      clearInterval(iv);
      ctx.close().catch(() => {});
    };
  }, [active]);
}

export const IncomingCall = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [incoming, setIncoming] = useState(null);

  const onEvent = useCallback((evt) => {
    window.dispatchEvent(new CustomEvent("chatmilu:callevent", { detail: evt }));
    if (evt.type === "ring" && evt.call) setIncoming(evt.call);
    else if (evt.type === "ring-cancel")
      setIncoming((c) => (c && c.call_id === evt.call_id ? null : c));
  }, []);

  useUserSocket(!!user, onEvent);
  useRingtone(!!incoming);

  useEffect(() => {
    if (!incoming) return;
    const t = setTimeout(() => setIncoming(null), RING_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [incoming]);

  if (!incoming) return null;

  const accept = () => {
    const room = incoming.room_id;
    const kind = incoming.video ? "video" : "voice";
    setIncoming(null);
    navigate(`/messages?room=${room}&call=${kind}`);
  };

  const decline = () => {
    api
      .post(`/rooms/${incoming.room_id}/calls/${incoming.call_id}/decline`)
      .catch((e) => console.debug("decline failed", e));
    setIncoming(null);
  };

  return (
    <div
      data-testid="incoming-call"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-6 backdrop-blur-sm"
    >
      <div className="w-full max-w-sm rounded-3xl border border-brand/30 bg-panel p-7 text-center shadow-2xl">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand/15">
          <span className="absolute h-16 w-16 animate-ping rounded-full bg-brand/20" />
          {incoming.video ? (
            <Video className="h-7 w-7 text-brand" />
          ) : (
            <Phone className="h-7 w-7 text-brand" />
          )}
        </span>
        <p className="mt-5 text-xs uppercase tracking-widest text-ink-soft">
          Incoming {incoming.video ? "video call" : "call"}
        </p>
        <p
          data-testid="incoming-call-from"
          className="mt-1 font-display text-2xl font-extrabold tracking-tight"
        >
          {incoming.started_by || "Someone"}
        </p>
        <p dir="auto" className="mt-1 text-sm text-ink-soft" data-testid="incoming-call-room">
          in “{incoming.room_name}”
        </p>
        <div className="mt-7 flex justify-center gap-3">
          <Button
            data-testid="decline-call-button"
            onClick={decline}
            className="min-h-[48px] flex-1 rounded-full bg-destructive hover:bg-destructive/90 active:scale-95"
          >
            <PhoneOff className="h-4 w-4" />
            <span className="ms-2">Decline</span>
          </Button>
          <Button
            data-testid="accept-call-button"
            onClick={accept}
            className="min-h-[48px] flex-1 rounded-full bg-brand hover:bg-brand-hover active:scale-95"
          >
            <Phone className="h-4 w-4" />
            <span className="ms-2">Accept</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
